import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Reusable page banner with breadcrumb + heading + subheading.
 */
export default function PageHero({ eyebrow, title, highlight, subtitle, breadcrumbs = [], img }) {
  return (
    <section className="relative bg-primary text-white overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={img || 'https://images.unsplash.com/photo-1547448415-e9f5b28e570d?w=1920&q=80'}
          alt="" className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
      </div>

      <div className="relative container mx-auto max-w-7xl px-4 md:px-8 pt-6 md:pt-8 pb-14 md:pb-20">
        {breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-white/70 mb-6">
            <Link to="/" className="hover:text-brand transition-colors">Home</Link>
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="w-3.5 h-3.5" />
                {b.href ? (
                  <Link to={b.href} className="hover:text-brand transition-colors">{b.label}</Link>
                ) : (
                  <span className="text-white font-semibold">{b.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        {eyebrow && (
          <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-brand font-bold">{eyebrow}</div>
        )}
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mt-3 leading-[1.05]">
          {title} {highlight && <span className="text-brand">{highlight}</span>}
        </h1>
        {subtitle && (
          <p className="mt-5 text-white/80 max-w-3xl text-base md:text-lg leading-relaxed">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export function Section({ eyebrow, title, highlight, subtitle, children, muted, id }) {
  return (
    <section id={id} className={`py-14 md:py-20 ${muted ? 'bg-muted/40' : 'bg-background'}`}>
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        {(eyebrow || title) && (
          <div className="max-w-3xl mb-10">
            {eyebrow && <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">{eyebrow}</div>}
            {title && (
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mt-2 leading-tight">
                {title} {highlight && <span className="text-brand">{highlight}</span>}
              </h2>
            )}
            {subtitle && <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">{subtitle}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export function InfoCard({ icon: Icon, title, children, num }) {
  return (
    <div className="p-5 rounded-2xl bg-white border border-border shadow-sm card-lift">
      <div className="flex items-center gap-3 mb-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
        )}
        {num && (
          <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shrink-0">{num}</div>
        )}
        <h4 className="font-serif text-lg font-bold text-primary">{title}</h4>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

export function CTABlock({ title, subtitle, cta = 'Talk to an Advisor', href, onClick }) {
  const clickHandler = onClick || (!href
    ? () => { try { window.dispatchEvent(new CustomEvent('open-inquiry-popup')); } catch (_) { /* ignore */ } }
    : undefined);
  return (
    <section className="py-14 bg-primary text-white">
      <div className="container mx-auto max-w-4xl px-4 md:px-8 text-center">
        <h3 className="font-serif text-3xl md:text-4xl font-bold">{title}</h3>
        {subtitle && <p className="mt-3 text-white/80 text-base md:text-lg">{subtitle}</p>}
        {clickHandler ? (
          <button onClick={clickHandler} data-testid="ctablock-open-popup"
            className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-7 h-12 rounded-full btn-shift transition-colors">
            {cta}
          </button>
        ) : href && href.startsWith('http') ? (
          <a href={href} target="_blank" rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-7 h-12 rounded-full btn-shift transition-colors">
            {cta}
          </a>
        ) : (
          <a href={href}
            className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-7 h-12 rounded-full btn-shift transition-colors">
            {cta}
          </a>
        )}
      </div>
    </section>
  );
}
